import { Text } from '@querodelivery/uni-react';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { useMemo } from 'react';
import styles from './motoboy-status.module.scss';
import { Timeline } from '@/Components/timeline/timeline';
import useMotoboy from '@/hooks/motoboy/useMotoboy';
import type { ENUM_DELIVERYMAN_STATUS } from '@/_constants/enums';
import { USER_STATE, type ILastState } from '@/Interfaces/Data';

interface TimelineData {
  steps: string[];
  currentStep: number;
}

function generateTimeline(lastState: ILastState | undefined): TimelineData {
  if (!lastState) {
    return { steps: [], currentStep: 0 };
  }

  const { page, currentPosition = 0, establishments = [] } = lastState;
  const totalEstablishments = establishments.length;

  if (totalEstablishments === 0 && page === USER_STATE.AGUARDANDO_FINALIZACAO) {
    return {
      steps: ['Finalizando Corrida'],
      currentStep: 0,
    };
  }

  if (totalEstablishments === 0) {
    return { steps: [], currentStep: 0 };
  }

  const allSteps: string[] = [];
  let currentStepIndex = 0;
  let found = false;

  const addStep = (
    stepText: string,
    targetPage: string | string[],
    targetPosition: number,
  ) => {
    allSteps.push(stepText);

    if (found) return;

    const targetPages = Array.isArray(targetPage) ? targetPage : [targetPage];
    const isCurrentPosition = targetPosition === currentPosition;

    if (isCurrentPosition && targetPages.includes(page)) {
      currentStepIndex = allSteps.length - 1;
      found = true;
      return;
    }

    if (targetPosition < currentPosition) {
      currentStepIndex = allSteps.length;
    }
  };

  const maxPosition = Math.max(...establishments.map(e => e.position));
  const sortedEstablishments = [...establishments].sort(
    (a, b) => a.position - b.position,
  );

  // FASE 1: Coleta - Vai em todos os estabelecimentos coletando entregas
  sortedEstablishments.forEach(establishment => {
    const { establishmentName, position } = establishment;

    addStep(
      `Indo até ${establishmentName}`,
      USER_STATE.DETALHES_DA_CORRIDA,
      position,
    );

    addStep(
      `Coletando entregas em ${establishmentName}`,
      USER_STATE.ENTREGAS_DISPONIVEIS,
      position,
    );
  });

  // FASE 2: Entrega - Escolhe e entrega para clientes
  addStep(
    'Escolhendo entrega para sair',
    [USER_STATE.SAIR_PARA_ENTREGAS, USER_STATE.PERSONALIZAR_CORRIDA],
    maxPosition,
  );

  addStep(
    'Indo até o cliente',
    USER_STATE.SAIR_PARA_ENTREGA_DETALHE,
    maxPosition,
  );

  addStep('Chegou no cliente', USER_STATE.FINALIZAR_ENTREGA, maxPosition);

  // FASE 3: Retorno - Escolhe estabelecimento e retorna para prestar contas
  if (totalEstablishments > 1) {
    addStep(
      'Escolhendo estabelecimento para retornar',
      USER_STATE.ESTABELECIMENTOS_PARA_RETORNAR,
      maxPosition,
    );
  }

  sortedEstablishments.forEach(establishment => {
    const { establishmentName, position } = establishment;

    addStep(
      `Retornando a ${establishmentName}`,
      USER_STATE.RETORNO_AO_ESTABELECIMENTO,
      position,
    );

    addStep(
      `Prestando contas em ${establishmentName}`,
      USER_STATE.AGUARDANDO_FINALIZACAO,
      position,
    );
  });

  return {
    steps: allSteps,
    currentStep: currentStepIndex,
  };
}

export const MotoboyStatus = () => {
  const { motoboyId } = useParams<{ motoboyId: string }>();

  const { loadingMotoboy, motoboy } = useMotoboy(motoboyId || '');

  const TEXT_BY_STATUS: Record<ENUM_DELIVERYMAN_STATUS, string> = {
    BLOQUEADO: 'Bloqueado',
    EXCLUIDO: 'Excluído',
    OFFLINE: 'Offline',
    PRECADASTRO: 'Pré-cadastro',
    REPROVADO: 'Reprovado',
    DISPONIVEL: 'Disponível',
    EM_CORRIDA: 'Em corrida',
    ENTREGA_ASSOCIADA: 'Em corrida',
  };

  const statusText = motoboy?.status
    ? TEXT_BY_STATUS[motoboy.status]
    : 'Offline';
  const isOnline = statusText === 'Disponível' || statusText === 'Em corrida';

  const timelineData = useMemo(() => {
    if (!isOnline || !motoboy?.lastState) {
      return { steps: [], currentStep: 0 };
    }
    return generateTimeline(motoboy.lastState);
  }, [isOnline, motoboy?.lastState]);

  const shouldShowTimeline = isOnline && timelineData.steps.length > 0;

  return (
    <div className={styles.statusContainer}>
      <div className={styles.statusContent}>
        <Text tone="highlight" fontSize="lg" fontWeight="semibold">
          Status do entregador
        </Text>
        <div
          className={classNames(styles.statusBadge, {
            [styles.OFFLINE]: !isOnline,
          })}
        >
          <Text
            tone={isOnline ? 'success' : 'error'}
            fontWeight="semibold"
            fontSize="md"
          >
            {statusText}
          </Text>
        </div>
      </div>
      {shouldShowTimeline && (
        <Timeline
          steps={timelineData.steps}
          currentStep={timelineData.currentStep}
          isLoading={loadingMotoboy}
        />
      )}
    </div>
  );
};
